import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { GoogleMap, DrawingManager, Marker, OverlayView } from '@react-google-maps/api';

// Format currency for map markers
function formatMarkerValue(value) {
  if (!value) return null;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return `${value}`;
}

// Cluster Marker Component - shows count when zoomed out
function ClusterMarker({ cluster, onClick }) {
  const { count, leads, center, hotCount } = cluster;

  // Size based on count
  const size = Math.min(60, 30 + Math.log10(count) * 15);

  // Color based on hot lead percentage
  const hotPercent = hotCount / count;
  const bgColor = hotPercent > 0.5 ? '#dc2626' : hotPercent > 0.2 ? '#f97316' : '#3b82f6';

  return (
    <div
      className="cluster-marker"
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(cluster);
      }}
      style={{
        '--cluster-size': `${size}px`,
        '--cluster-color': bgColor
      }}
    >
      <div className="cluster-count">{count > 999 ? `${Math.round(count / 1000)}K` : count}</div>
      {hotCount > 0 && <div className="cluster-hot-badge">{hotCount} hot</div>}
    </div>
  );
}

// Price Marker Component
function PriceMarker({ lead, onClick, isSelected }) {
  const value = lead.assessed_value || lead.assessedValue || lead.roof_cost_low || lead.roofCostAsphalt;
  const displayValue = formatMarkerValue(value);

  if (!displayValue) return null;

  const isHot = lead.priority === 'hot' || lead.leadQuality === 'hot';
  const isEnriched = lead.enriched;
  const needsRoof = lead.roof_needs_replacement || lead.needsRoofReplacement;

  // Color based on status
  let bgColor = '#6b7280'; // gray default
  if (needsRoof) bgColor = '#dc2626'; // red for needs roof
  else if (isHot) bgColor = '#ef4444'; // red for hot
  else if (isEnriched) bgColor = '#22c55e'; // green for enriched
  else bgColor = '#3b82f6'; // blue default

  return (
    <div
      className={`price-marker ${isSelected ? 'selected' : ''} ${isHot ? 'hot' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(lead);
      }}
      style={{ '--marker-color': bgColor }}
    >
      <span className="price-value">${displayValue}</span>
      {needsRoof && <span className="needs-roof-dot" title="Needs roof replacement"></span>}
    </div>
  );
}

// Simple clustering algorithm - groups nearby leads based on zoom level
function clusterLeads(leads, mapBounds, zoom) {
  if (!leads || leads.length === 0) return { clusters: [], unclustered: [] };

  // Filter leads with coordinates
  const leadsWithCoords = leads.filter(l => l.lat && l.lng);
  if (leadsWithCoords.length === 0) return { clusters: [], unclustered: [] };

  // If no zoom yet, use default
  const effectiveZoom = zoom || 13;

  // Cluster radius in degrees based on zoom level
  // Lower zoom = bigger clusters, higher zoom = smaller/no clusters
  const clusterRadius = effectiveZoom < 12 ? 0.03 : effectiveZoom < 14 ? 0.015 : effectiveZoom < 16 ? 0.005 : 0;

  // If zoomed in enough, show individual markers
  if (clusterRadius === 0) {
    return { clusters: [], unclustered: leadsWithCoords };
  }

  const clusters = [];
  const used = new Set();

  for (let i = 0; i < leadsWithCoords.length; i++) {
    if (used.has(i)) continue;

    const lead = leadsWithCoords[i];
    const clusterLeads = [lead];
    used.add(i);

    // Find nearby leads to cluster
    for (let j = i + 1; j < leadsWithCoords.length; j++) {
      if (used.has(j)) continue;

      const other = leadsWithCoords[j];
      const distance = Math.sqrt(
        Math.pow(lead.lat - other.lat, 2) + Math.pow(lead.lng - other.lng, 2)
      );

      if (distance < clusterRadius) {
        clusterLeads.push(other);
        used.add(j);
      }
    }

    // Only create cluster if multiple leads
    if (clusterLeads.length >= 3) {
      // Calculate cluster center
      const centerLat = clusterLeads.reduce((sum, l) => sum + l.lat, 0) / clusterLeads.length;
      const centerLng = clusterLeads.reduce((sum, l) => sum + l.lng, 0) / clusterLeads.length;

      clusters.push({
        center: { lat: centerLat, lng: centerLng },
        leads: clusterLeads,
        count: clusterLeads.length,
        hotCount: clusterLeads.filter(l => l.leadQuality === 'hot' || l.priority === 'hot').length
      });
    } else {
      // Add as individual markers
      clusters.push({
        center: { lat: lead.lat, lng: lead.lng },
        leads: clusterLeads,
        count: 1,
        hotCount: 0,
        isSingle: true
      });
    }
  }

  // Separate clusters from singles
  const realClusters = clusters.filter(c => !c.isSingle);
  const unclustered = clusters.filter(c => c.isSingle).flatMap(c => c.leads);

  return { clusters: realClusters, unclustered };
}

// Update map options when drawing mode changes

const getMapContainerStyle = (isDrawing) => ({
  width: '100%',
  height: '100%',
  minHeight: '100%',
  cursor: isDrawing ? 'crosshair' : 'grab'
});

const getMapOptions = (isDrawing) => ({
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: false,
  draggable: !isDrawing,
  scrollwheel: !isDrawing,
  disableDoubleClickZoom: isDrawing,
  clickableIcons: false,
  mapTypeId: 'hybrid',
  styles: [
    // Hide POIs except for important ones
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
    // Keep parks visible but subtle
    { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    // Make sure roads and addresses are visible
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    // Subtle color adjustments for professional look
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e4f5' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    // Transit off
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ]
});

const drawingOptions = {
  circleOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: false,  // No resize handles (cleaner look)
    draggable: true,  // Can still move
    zIndex: 1
  },
  rectangleOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: true,
    draggable: true,
    zIndex: 1
  },
  polygonOptions: {
    fillColor: '#3b82f6',
    fillOpacity: 0.25,
    strokeWeight: 3,
    strokeColor: '#60a5fa',
    clickable: true,
    editable: false,
    draggable: true,
    zIndex: 1
  }
};

function MapComponent({
  center,
  initialZoom = 17,
  drawingMode,
  setDrawingMode,
  onLeadsFound,
  setIsLoading,
  mapRef,
  onLocateMe,
  currentStep,
  setCurrentStep,
  trackApiCall,
  selectedLead,
  onMapIdle,
  dbLeads = [],
  leads = [], // Current session leads (from hotspots or drawing)
  selectedLeadIndices = new Set(),
  onPropertyClick,
  onLeadClick // Click handler for session leads
}) {
  const [currentShape, setCurrentShape] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [mapBounds, setMapBounds] = useState(null);

  // Custom circle drawing state
  const isDrawingCircle = useRef(false);
  const circleStartPoint = useRef(null);
  const previewCircle = useRef(null);

  // Lasso (freeform) drawing state
  const isDrawingLasso = useRef(false);
  const lassoPoints = useRef([]);
  const previewPolyline = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;

    // Set initial zoom and bounds
    setCurrentZoom(map.getZoom());
    setMapBounds(map.getBounds());

    // Listen for zoom changes
    map.addListener('zoom_changed', () => {
      setCurrentZoom(map.getZoom());
    });

    // Listen for bounds changes (for clustering visibility)
    map.addListener('bounds_changed', () => {
      setMapBounds(map.getBounds());
    });
  }, [mapRef]);

  // Compute clusters for session leads
  const { clusters: leadClusters, unclustered: unclusteredLeads } = useMemo(() => {
    const result = clusterLeads(leads, mapBounds, currentZoom);
    if (leads.length > 0) {
      console.log(`📍 Clustering: ${leads.length} leads → ${result.clusters.length} clusters, ${result.unclustered.length} individual markers (zoom: ${currentZoom})`);
    }
    return result;
  }, [leads, mapBounds, currentZoom]);

  // Handle cluster click - zoom in to show individual markers
  const handleClusterClick = useCallback((cluster) => {
    if (!mapRef.current) return;

    // Calculate bounds that contains all leads in the cluster
    const bounds = new window.google.maps.LatLngBounds();
    cluster.leads.forEach(lead => {
      bounds.extend({ lat: lead.lat, lng: lead.lng });
    });

    // Fit map to show all leads in cluster with some padding
    mapRef.current.fitBounds(bounds, { padding: 50 });

    // Ensure we zoom in enough to see individual markers
    const newZoom = mapRef.current.getZoom();
    if (newZoom < 16) {
      mapRef.current.setZoom(16);
    }
  }, [mapRef]);

  // Disable map dragging when in drawing mode
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({
        draggable: !drawingMode,
        scrollwheel: !drawingMode,
        disableDoubleClickZoom: !!drawingMode
      });
    }
  }, [drawingMode, mapRef]);

  // Center and zoom on selected lead
  useEffect(() => {
    console.log('=== Selected Lead Changed ===');
    console.log('selectedLead:', selectedLead?.streetAddress, 'lat:', selectedLead?.lat, 'lng:', selectedLead?.lng);
    if (selectedLead && selectedLead.lat && selectedLead.lng && mapRef.current) {
      console.log('Panning to lead location');
      mapRef.current.panTo({ lat: selectedLead.lat, lng: selectedLead.lng });
      mapRef.current.setZoom(19); // Close zoom to see the house
    }
  }, [selectedLead, mapRef]);

  const clearShape = useCallback(() => {
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
    }
    onLeadsFound([]);
    setDrawingMode(null);
    setCurrentStep(2);
  }, [currentShape, onLeadsFound, setDrawingMode, setCurrentStep]);

  const getAddressesInBounds = useCallback(async (bounds) => {
    setIsLoading(true);

    const geocoder = new window.google.maps.Geocoder();
    const addresses = [];

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Calculate area size in meters for adaptive grid density
    const latDist = Math.abs(ne.lat() - sw.lat()) * 111320; // ~111km per degree
    const lngDist = Math.abs(ne.lng() - sw.lng()) * 111320 * Math.cos(((ne.lat() + sw.lat()) / 2) * Math.PI / 180);
    const areaSqMeters = latDist * lngDist;

    // Adaptive grid: denser for smaller areas to ensure we catch individual houses
    // Typical residential lot is ~500-1000 sq meters, house footprint ~150-300 sq meters
    let gridSize;
    if (areaSqMeters < 500) { // Single house
      gridSize = 3; // 9 points - more likely to hit center
    } else if (areaSqMeters < 1500) { // 1-2 houses
      gridSize = 4; // 16 points
    } else if (areaSqMeters < 4000) { // 3-5 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 10000) { // Small: 5-10 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 25000) { // Medium: 10-25 houses
      gridSize = 6; // 36 points
    } else if (areaSqMeters < 60000) { // Large: neighborhood block
      gridSize = 7; // 49 points
    } else if (areaSqMeters < 200000) { // Very large
      gridSize = 8; // 64 points
    } else {
      gridSize = 10; // Huge area: 100 points
    }

    // Calculate step to get exactly gridSize points per dimension
    // We want gridSize points spread across the area (including edges)
    const latStep = gridSize > 1 ? (ne.lat() - sw.lat()) / (gridSize - 1) : 0;
    const lngStep = gridSize > 1 ? (ne.lng() - sw.lng()) / (gridSize - 1) : 0;

    console.log('=== Geocoding Debug ===');
    console.log('Bounds:', { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } });
    console.log('Area:', Math.round(areaSqMeters), 'sq meters, Grid:', gridSize, 'x', gridSize, '=', gridSize * gridSize, 'points');

    const promises = [];

    // Generate exactly gridSize x gridSize sample points
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const lat = sw.lat() + i * latStep;
        const lng = sw.lng() + j * lngStep;
        const location = { lat, lng };

        promises.push(
          new Promise((resolve) => {
            geocoder.geocode({ location }, (results, status) => {
              // Track API call
              if (trackApiCall) trackApiCall('geocoding');

              if (status === 'OK' && results && results.length > 0) {
                // Only accept ROOFTOP precision (actual addresses, not interpolated)
                // RANGE_INTERPOLATED = Google guessed the address (fake!)
                // ROOFTOP = Actual verified address location
                const streetAddresses = results.filter(r =>
                  (r.types.includes('street_address') ||
                   r.types.includes('premise') ||
                   r.types.includes('subpremise')) &&
                  r.geometry.location_type === 'ROOFTOP'
                );

                streetAddresses.forEach(result => {
                  const components = result.address_components;
                  const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
                  const route = components.find(c => c.types.includes('route'))?.long_name || '';
                  const city = components.find(c => c.types.includes('locality'))?.long_name ||
                              components.find(c => c.types.includes('sublocality'))?.long_name || '';
                  const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                  const zip = components.find(c => c.types.includes('postal_code'))?.long_name || '';

                  const addrLat = result.geometry.location.lat();
                  const addrLng = result.geometry.location.lng();

                  // Only include if address is actually INSIDE the drawn bounds
                  const isInsideBounds =
                    addrLat >= sw.lat() && addrLat <= ne.lat() &&
                    addrLng >= sw.lng() && addrLng <= ne.lng();

                  if (streetNumber && route && isInsideBounds) {
                    addresses.push({
                      streetAddress: `${streetNumber} ${route}`,
                      city,
                      state,
                      zip,
                      fullAddress: result.formatted_address,
                      lat: addrLat,
                      lng: addrLng,
                      placeId: result.place_id
                    });
                  }
                });
              }
              resolve();
            });
          })
        );
      }
    }

    await Promise.all(promises);

    // Deduplicate by street address (not placeId since same address can have multiple placeIds)
    const uniqueAddresses = addresses.filter((addr, index, self) =>
      index === self.findIndex(a => a.streetAddress === addr.streetAddress)
    );

    // Sort by street number numerically
    uniqueAddresses.sort((a, b) => {
      const numA = parseInt(a.streetAddress) || 0;
      const numB = parseInt(b.streetAddress) || 0;
      return numA - numB;
    });

    console.log('Raw addresses found:', addresses.length);
    console.log('Unique addresses:', uniqueAddresses.length);
    console.log('Addresses:', uniqueAddresses.map(a => a.streetAddress));

    setIsLoading(false);
    onLeadsFound(uniqueAddresses);
  }, [onLeadsFound, setIsLoading, trackApiCall]);

  // Check if a point is inside a polygon using ray casting
  const isPointInPolygon = useCallback((point, polygon) => {
    const path = polygon.getPath().getArray();
    let inside = false;
    const x = point.lng, y = point.lat;

    for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
      const xi = path[i].lng(), yi = path[i].lat();
      const xj = path[j].lng(), yj = path[j].lat();

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  // Get addresses within a polygon (lasso)
  const getAddressesInPolygon = useCallback(async (polygon) => {
    setIsLoading(true);

    const geocoder = new window.google.maps.Geocoder();
    const addresses = [];

    // Get bounding box of polygon
    const bounds = new window.google.maps.LatLngBounds();
    polygon.getPath().forEach(point => bounds.extend(point));

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Calculate area size in meters for adaptive grid density
    const latDist = Math.abs(ne.lat() - sw.lat()) * 111320;
    const lngDist = Math.abs(ne.lng() - sw.lng()) * 111320 * Math.cos(((ne.lat() + sw.lat()) / 2) * Math.PI / 180);
    const areaSqMeters = latDist * lngDist;

    // Adaptive grid: denser for smaller areas to ensure we catch individual houses
    let gridSize;
    if (areaSqMeters < 500) { // Single house
      gridSize = 3; // 9 points - more likely to hit center
    } else if (areaSqMeters < 1500) { // 1-2 houses
      gridSize = 4; // 16 points
    } else if (areaSqMeters < 4000) { // 3-5 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 10000) { // Small: 5-10 houses
      gridSize = 5; // 25 points
    } else if (areaSqMeters < 25000) { // Medium: 10-25 houses
      gridSize = 6; // 36 points
    } else if (areaSqMeters < 60000) { // Large: neighborhood block
      gridSize = 7; // 49 points
    } else if (areaSqMeters < 200000) { // Very large
      gridSize = 8; // 64 points
    } else {
      gridSize = 10; // Huge area: 100 points
    }

    // Calculate step to get exactly gridSize points per dimension
    const latStep = gridSize > 1 ? (ne.lat() - sw.lat()) / (gridSize - 1) : 0;
    const lngStep = gridSize > 1 ? (ne.lng() - sw.lng()) / (gridSize - 1) : 0;

    console.log('=== Lasso Geocoding Debug ===');
    console.log('Polygon bounds:', { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } });
    console.log('Area:', Math.round(areaSqMeters), 'sq meters, Grid:', gridSize, 'x', gridSize, '=', gridSize * gridSize, 'points');

    const promises = [];

    // Generate exactly gridSize x gridSize sample points
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const lat = sw.lat() + i * latStep;
        const lng = sw.lng() + j * lngStep;

        // Only geocode points inside the polygon
        if (!isPointInPolygon({ lat, lng }, polygon)) continue;

        const location = { lat, lng };

        promises.push(
          new Promise((resolve) => {
            geocoder.geocode({ location }, (results, status) => {
              // Track API call
              if (trackApiCall) trackApiCall('geocoding');

              if (status === 'OK' && results && results.length > 0) {
                // Only accept ROOFTOP precision (actual addresses, not interpolated)
                // RANGE_INTERPOLATED = Google guessed the address (fake!)
                // ROOFTOP = Actual verified address location
                const streetAddresses = results.filter(r =>
                  (r.types.includes('street_address') ||
                   r.types.includes('premise') ||
                   r.types.includes('subpremise')) &&
                  r.geometry.location_type === 'ROOFTOP'
                );

                streetAddresses.forEach(result => {
                  const components = result.address_components;
                  const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
                  const route = components.find(c => c.types.includes('route'))?.long_name || '';
                  const city = components.find(c => c.types.includes('locality'))?.long_name ||
                              components.find(c => c.types.includes('sublocality'))?.long_name || '';
                  const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                  const zip = components.find(c => c.types.includes('postal_code'))?.long_name || '';

                  const addrLat = result.geometry.location.lat();
                  const addrLng = result.geometry.location.lng();

                  // Only include if address is actually INSIDE the polygon
                  const isInsidePolygon = isPointInPolygon({ lat: addrLat, lng: addrLng }, polygon);

                  if (streetNumber && route && isInsidePolygon) {
                    addresses.push({
                      streetAddress: `${streetNumber} ${route}`,
                      city,
                      state,
                      zip,
                      fullAddress: result.formatted_address,
                      lat: addrLat,
                      lng: addrLng,
                      placeId: result.place_id
                    });
                  }
                });
              }
              resolve();
            });
          })
        );
      }
    }

    await Promise.all(promises);

    // Deduplicate by street address
    const uniqueAddresses = addresses.filter((addr, index, self) =>
      index === self.findIndex(a => a.streetAddress === addr.streetAddress)
    );

    // Sort by street number numerically
    uniqueAddresses.sort((a, b) => {
      const numA = parseInt(a.streetAddress) || 0;
      const numB = parseInt(b.streetAddress) || 0;
      return numA - numB;
    });

    console.log('Unique addresses in polygon:', uniqueAddresses.length);

    setIsLoading(false);
    onLeadsFound(uniqueAddresses);
  }, [onLeadsFound, setIsLoading, isPointInPolygon, trackApiCall]);

  const onPolygonComplete = useCallback((polygon) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(polygon);
    setDrawingMode(null);

    getAddressesInPolygon(polygon);
  }, [currentShape, setDrawingMode, getAddressesInPolygon]);

  const onCircleComplete = useCallback((circle) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(circle);
    setDrawingMode(null);

    const bounds = circle.getBounds();
    getAddressesInBounds(bounds);

    circle.addListener('radius_changed', () => {
      getAddressesInBounds(circle.getBounds());
    });
    circle.addListener('center_changed', () => {
      getAddressesInBounds(circle.getBounds());
    });
  }, [currentShape, setDrawingMode, getAddressesInBounds]);

  const onRectangleComplete = useCallback((rectangle) => {
    if (currentShape) {
      currentShape.setMap(null);
    }
    setCurrentShape(rectangle);
    setDrawingMode(null);

    const bounds = rectangle.getBounds();
    getAddressesInBounds(bounds);

    rectangle.addListener('bounds_changed', () => {
      getAddressesInBounds(rectangle.getBounds());
    });
  }, [currentShape, setDrawingMode, getAddressesInBounds]);

  const handleDrawClick = (mode) => {
    // Clear existing shape when entering drawing mode
    if (mode && currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }
    setDrawingMode(drawingMode === mode ? null : mode);
    if (currentStep === 1) setCurrentStep(2);
  };

  // Lasso (freeform) drawing handlers
  const startLassoDrawing = useCallback((latLng) => {
    if (drawingMode !== 'lasso') return;

    // Clear existing shape and data
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }

    isDrawingLasso.current = true;
    lassoPoints.current = [{ lat: latLng.lat(), lng: latLng.lng() }];

    // Create preview polyline
    if (previewPolyline.current) {
      previewPolyline.current.setMap(null);
    }

    previewPolyline.current = new window.google.maps.Polyline({
      map: mapRef.current,
      path: lassoPoints.current,
      strokeColor: '#3b82f6',
      strokeWeight: 3,
      strokeOpacity: 0.8
    });
  }, [drawingMode, mapRef, currentShape, onLeadsFound]);

  const updateLassoDrawing = useCallback((latLng) => {
    if (!isDrawingLasso.current || !previewPolyline.current) return;

    lassoPoints.current.push({ lat: latLng.lat(), lng: latLng.lng() });
    previewPolyline.current.setPath(lassoPoints.current);
  }, []);

  const finishLassoDrawing = useCallback(() => {
    if (!isDrawingLasso.current) return;

    isDrawingLasso.current = false;

    // Remove preview polyline
    if (previewPolyline.current) {
      previewPolyline.current.setMap(null);
      previewPolyline.current = null;
    }

    // Need at least 3 points for a polygon
    if (lassoPoints.current.length < 3) {
      lassoPoints.current = [];
      return;
    }

    // Simplify path (reduce points for better performance)
    // Keep more points for small shapes, fewer for large ones
    const skipFactor = lassoPoints.current.length > 50 ? 4 :
                       lassoPoints.current.length > 20 ? 3 :
                       lassoPoints.current.length > 10 ? 2 : 1;
    const simplifiedPath = lassoPoints.current.filter((_, i) =>
      i % skipFactor === 0 || i === lassoPoints.current.length - 1
    );

    // Create final polygon
    const polygon = new window.google.maps.Polygon({
      map: mapRef.current,
      paths: simplifiedPath,
      ...drawingOptions.polygonOptions
    });

    lassoPoints.current = [];
    onPolygonComplete(polygon);
  }, [mapRef, onPolygonComplete]);

  // Custom circle drawing - drag from corner to corner like rectangle
  // Works with both mouse (desktop) and touch (mobile)
  const startCircleDrawing = useCallback((latLng) => {
    if (drawingMode !== 'circle') return;

    // Clear existing shape and data when starting new drawing
    if (currentShape) {
      currentShape.setMap(null);
      setCurrentShape(null);
      onLeadsFound([]);
    }

    isDrawingCircle.current = true;
    circleStartPoint.current = {
      lat: latLng.lat(),
      lng: latLng.lng()
    };

    // Create preview circle
    if (previewCircle.current) {
      previewCircle.current.setMap(null);
    }

    previewCircle.current = new window.google.maps.Circle({
      map: mapRef.current,
      center: circleStartPoint.current,
      radius: 1,
      fillColor: '#3b82f6',
      fillOpacity: 0.2,
      strokeWeight: 2,
      strokeColor: '#3b82f6',
      clickable: false,
      editable: false,
      draggable: false,
      zIndex: 1
    });
  }, [drawingMode, mapRef, currentShape, onLeadsFound]);

  // Update circle preview during drag (mouse or touch)
  const updateCircleDrawing = useCallback((latLng) => {
    if (!isDrawingCircle.current || !circleStartPoint.current || !previewCircle.current) return;

    const start = circleStartPoint.current;
    const endLat = latLng.lat();
    const endLng = latLng.lng();

    // Calculate center as midpoint between start and end
    const centerLat = (start.lat + endLat) / 2;
    const centerLng = (start.lng + endLng) / 2;

    // Calculate radius as half the diagonal distance
    const latDiff = Math.abs(endLat - start.lat);
    const lngDiff = Math.abs(endLng - start.lng);

    // Convert to meters (approximate)
    const latMeters = latDiff * 111320;
    const lngMeters = lngDiff * 111320 * Math.cos(centerLat * Math.PI / 180);

    // Use half the diagonal for radius
    const radius = Math.sqrt(latMeters * latMeters + lngMeters * lngMeters) / 2;

    // Batch updates for better performance
    previewCircle.current.setOptions({
      center: { lat: centerLat, lng: centerLng },
      radius: radius
    });
  }, []);

  // Finish circle drawing (mouse or touch)
  const finishCircleDrawing = useCallback((latLng) => {
    if (!isDrawingCircle.current || !circleStartPoint.current) return;

    isDrawingCircle.current = false;

    const start = circleStartPoint.current;
    const endLat = latLng.lat();
    const endLng = latLng.lng();

    // Calculate final circle
    const centerLat = (start.lat + endLat) / 2;
    const centerLng = (start.lng + endLng) / 2;

    const latDiff = Math.abs(endLat - start.lat);
    const lngDiff = Math.abs(endLng - start.lng);

    const latMeters = latDiff * 111320;
    const lngMeters = lngDiff * 111320 * Math.cos(centerLat * Math.PI / 180);
    const radius = Math.sqrt(latMeters * latMeters + lngMeters * lngMeters) / 2;

    // Remove preview
    if (previewCircle.current) {
      previewCircle.current.setMap(null);
      previewCircle.current = null;
    }

    // Don't create if too small
    if (radius < 10) {
      circleStartPoint.current = null;
      return;
    }

    // Create final editable circle
    const circle = new window.google.maps.Circle({
      map: mapRef.current,
      center: { lat: centerLat, lng: centerLng },
      radius: radius,
      ...drawingOptions.circleOptions
    });

    circleStartPoint.current = null;

    // Use existing onCircleComplete handler
    onCircleComplete(circle);
  }, [mapRef, onCircleComplete]);

  // Mouse and Touch event support for lasso/circle drawing
  // Using native DOM events for reliable continuous drawing
  useEffect(() => {
    if (!mapRef.current || (drawingMode !== 'circle' && drawingMode !== 'lasso')) return;

    const map = mapRef.current;
    const mapDiv = map.getDiv();

    const getLatLngFromMouse = (e) => {
      const rect = mapDiv.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const bounds = map.getBounds();
      if (!bounds) return null;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const lng = sw.lng() + (x / rect.width) * (ne.lng() - sw.lng());
      const lat = ne.lat() - (y / rect.height) * (ne.lat() - sw.lat());

      return {
        lat: () => lat,
        lng: () => lng
      };
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0) return; // Left click only
      e.preventDefault();

      const latLng = getLatLngFromMouse(e);
      if (!latLng) return;

      if (drawingMode === 'circle') {
        startCircleDrawing(latLng);
      } else if (drawingMode === 'lasso') {
        startLassoDrawing(latLng);
      }
    };

    const handleMouseMove = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      const latLng = getLatLngFromMouse(e);
      if (!latLng) return;

      if (isDrawingCircle.current) {
        updateCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        updateLassoDrawing(latLng);
      }
    };

    const handleMouseUp = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      if (isDrawingCircle.current) {
        const latLng = getLatLngFromMouse(e);
        if (latLng) finishCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        finishLassoDrawing();
      }
    };

    // Add mouse listeners
    mapDiv.addEventListener('mousedown', handleMouseDown);
    mapDiv.addEventListener('mousemove', handleMouseMove);
    mapDiv.addEventListener('mouseup', handleMouseUp);
    mapDiv.addEventListener('mouseleave', handleMouseUp); // Finish if mouse leaves map

    const getLatLngFromTouch = (touch) => {
      const rect = mapDiv.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      // Convert pixel coordinates to lat/lng
      const projection = map.getProjection();
      if (!projection) return null;

      const bounds = map.getBounds();
      if (!bounds) return null;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const lng = sw.lng() + (x / rect.width) * (ne.lng() - sw.lng());
      const lat = ne.lat() - (y / rect.height) * (ne.lat() - sw.lat());

      return {
        lat: () => lat,
        lng: () => lng
      };
    };

    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();

      const latLng = getLatLngFromTouch(e.touches[0]);
      if (!latLng) return;

      if (drawingMode === 'circle') {
        startCircleDrawing(latLng);
      } else if (drawingMode === 'lasso') {
        startLassoDrawing(latLng);
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      const latLng = getLatLngFromTouch(e.touches[0]);
      if (!latLng) return;

      if (isDrawingCircle.current) {
        updateCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        updateLassoDrawing(latLng);
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDrawingCircle.current && !isDrawingLasso.current) return;
      e.preventDefault();

      if (isDrawingCircle.current) {
        const latLng = getLatLngFromTouch(e.changedTouches[0]);
        if (latLng) finishCircleDrawing(latLng);
      } else if (isDrawingLasso.current) {
        finishLassoDrawing();
      }
    };

    // Add touch listeners with passive: false to allow preventDefault
    mapDiv.addEventListener('touchstart', handleTouchStart, { passive: false });
    mapDiv.addEventListener('touchmove', handleTouchMove, { passive: false });
    mapDiv.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      // Remove mouse listeners
      mapDiv.removeEventListener('mousedown', handleMouseDown);
      mapDiv.removeEventListener('mousemove', handleMouseMove);
      mapDiv.removeEventListener('mouseup', handleMouseUp);
      mapDiv.removeEventListener('mouseleave', handleMouseUp);
      // Remove touch listeners
      mapDiv.removeEventListener('touchstart', handleTouchStart);
      mapDiv.removeEventListener('touchmove', handleTouchMove);
      mapDiv.removeEventListener('touchend', handleTouchEnd);
    };
  }, [drawingMode, mapRef, startCircleDrawing, updateCircleDrawing, finishCircleDrawing, startLassoDrawing, updateLassoDrawing, finishLassoDrawing]);

  return (
    <>
      {/* Drawing Controls */}
      <div className="drawing-controls">
        <button
          className={`draw-btn ${drawingMode === 'circle' ? 'active' : ''}`}
          onClick={() => handleDrawClick('circle')}
        >
          <div className="draw-btn-icon">
            <img src="/images/icon-circle.webp" alt="Circle" />
          </div>
          <span>Circle</span>
        </button>

        <button
          className={`draw-btn ${drawingMode === 'rectangle' ? 'active' : ''}`}
          onClick={() => handleDrawClick('rectangle')}
        >
          <div className="draw-btn-icon">
            <img src="/images/icon-rectangle.webp" alt="Rectangle" />
          </div>
          <span>Rectangle</span>
        </button>

        <button
          className={`draw-btn ${drawingMode === 'lasso' ? 'active' : ''}`}
          onClick={() => handleDrawClick('lasso')}
        >
          <div className="draw-btn-icon">
            <span style={{ fontSize: '1.25rem' }}>✏️</span>
          </div>
          <span>Lasso</span>
        </button>

        <button className="draw-btn" onClick={onLocateMe}>
          <div className="draw-btn-icon">
            <img src="/images/icon-location.webp" alt="Location" />
          </div>
          <span>My Location</span>
        </button>

        {currentShape && (
          <button className="draw-btn clear" onClick={clearShape}>
            <div className="draw-btn-icon">
              <span>🗑️</span>
            </div>
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Map */}
      <GoogleMap
        mapContainerStyle={getMapContainerStyle(!!drawingMode)}
        center={center}
        zoom={initialZoom}
        onLoad={onLoad}
        onIdle={onMapIdle}
        options={getMapOptions(!!drawingMode)}
      >
        {/* Only use DrawingManager for rectangle, circle is custom */}
        {drawingMode === 'rectangle' && (
          <DrawingManager
            drawingMode={window.google.maps.drawing.OverlayType.RECTANGLE}
            options={{
              drawingControl: false,
              rectangleOptions: drawingOptions.rectangleOptions
            }}
            onRectangleComplete={(rect) => {
              console.log('Rectangle drawn', rect);
              onRectangleComplete(rect);
            }}
          />
        )}

        {/* DB leads - Price markers on map (like Redfin) */}
        {dbLeads.filter(l => l.lat && l.lng).map((lead, index) => (
          <OverlayView
            key={`db-${lead.id || index}`}
            position={{ lat: lead.lat, lng: lead.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <PriceMarker
              lead={lead}
              onClick={onPropertyClick}
              isSelected={selectedLead?.id === lead.id}
            />
          </OverlayView>
        ))}

        {/* Session leads - Cluster markers when zoomed out */}
        {leadClusters.map((cluster, index) => (
          <OverlayView
            key={`cluster-${index}`}
            position={cluster.center}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <ClusterMarker
              cluster={cluster}
              onClick={handleClusterClick}
            />
          </OverlayView>
        ))}

        {/* Session leads - Individual price markers when zoomed in */}
        {unclusteredLeads.map((lead, index) => {
          // Find original index in leads array for selection tracking
          const originalIndex = leads.findIndex(l =>
            l.streetAddress === lead.streetAddress && l.zip === lead.zip
          );
          return (
            <OverlayView
              key={`lead-${lead.placeId || index}`}
              position={{ lat: lead.lat, lng: lead.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <PriceMarker
                lead={lead}
                onClick={() => onLeadClick && onLeadClick(lead, originalIndex >= 0 ? originalIndex : index)}
                isSelected={selectedLeadIndices.has(originalIndex) || selectedLead?.streetAddress === lead.streetAddress}
              />
            </OverlayView>
          );
        })}

        {/* Selected lead marker */}
        {selectedLead && selectedLead.lat && selectedLead.lng && (
          <Marker
            position={{ lat: selectedLead.lat, lng: selectedLead.lng }}
            label={{
              text: '★',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
            icon={{
              path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
              fillColor: selectedLead.leadQuality === 'hot' ? '#ef4444' : '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
              scale: 2.5,
              anchor: new window.google.maps.Point(12, 22),
              labelOrigin: new window.google.maps.Point(12, 10)
            }}
            title={selectedLead.streetAddress}
            animation={window.google.maps.Animation.DROP}
          />
        )}
      </GoogleMap>

      {/* Step Indicator */}
      <div className="step-indicator">
        <div className={`step ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : ''}`}>
          <div className="step-number">{currentStep > 1 ? '✓' : '1'}</div>
          <span className="step-text">Find Area</span>
        </div>
        <div className={`step-divider ${currentStep > 1 ? 'completed' : ''}`}></div>
        <div className={`step ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : ''}`}>
          <div className="step-number">{currentStep > 2 ? '✓' : '2'}</div>
          <span className="step-text">Draw Selection</span>
        </div>
        <div className={`step-divider ${currentStep > 2 ? 'completed' : ''}`}></div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <span className="step-text">Export Leads</span>
        </div>
      </div>
    </>
  );
}

export default MapComponent;
